import { useEffect, useState } from 'react'
import { Link, useOutletContext } from 'react-router-dom'

import { listAbTests, setAbTestStatus } from '../../../api/abTests.js'
import { listGroupConfigSegments, listGroupConfigs } from '../../../api/groupConfigs.js'
import { listSegmentations } from '../../../api/segmentations.js'
import { canTransitionAbTestStatus, getAvailableAbTestStatuses } from '../../../local/abTestStatusStateMachine.js'
import { useFeedback } from '../../../components/ui/FeedbackProvider.jsx'

function formatDate(value) {
  if (!value) return ''
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleString()
}

function getGroupSegmentMapKey(groupConfigId, segmentKey) {
  return `${String(groupConfigId || '')}::${String(segmentKey || '')}`
}

export default function ApplicationAbTestsListPage() {
  const { applicationId } = useOutletContext()
  const { toast } = useFeedback()
  const [filterGroupConfigId, setFilterGroupConfigId] = useState('')
  const [state, setState] = useState({
    status: 'loading',
    data: [],
    groupConfigs: [],
    groupNamesById: {},
    segmentNamesByGroupSegment: {},
    segmentNamesBySegmentKey: {},
    error: null,
  })

  useEffect(() => {
    let cancelled = false

    setState({
      status: 'loading',
      data: [],
      groupConfigs: [],
      groupNamesById: {},
      segmentNamesByGroupSegment: {},
      segmentNamesBySegmentKey: {},
      error: null,
    })

    Promise.all([listAbTests(applicationId), listGroupConfigs(applicationId), listSegmentations(applicationId)])
      .then(async ([abTests, groupConfigs, segmentations]) => {
        const groupNamesById = Object.fromEntries(
          groupConfigs.map((groupConfig) => [String(groupConfig.id), groupConfig.name || String(groupConfig.id)]),
        )
        const segmentationNamesById = Object.fromEntries(
          segmentations.map((segmentation) => [String(segmentation.id), segmentation.name || segmentation.id]),
        )

        const groupSegmentsList = await Promise.all(
          groupConfigs.map(async (groupConfig) => {
            try {
              const segments = await listGroupConfigSegments(applicationId, groupConfig.id)
              return { groupConfigId: groupConfig.id, segments }
            } catch {
              return { groupConfigId: groupConfig.id, segments: [] }
            }
          }),
        )

        const segmentNamesByGroupSegment = {}
        const segmentNamesBySegmentKey = {}
        groupSegmentsList.forEach(({ groupConfigId, segments }) => {
          segments.forEach((groupSegment) => {
            const key = getGroupSegmentMapKey(groupConfigId, groupSegment.segmentKey)
            const segmentName =
              segmentationNamesById[String(groupSegment.segmentationId)] ||
              groupSegment.segmentKey ||
              String(groupSegment.id)
            segmentNamesByGroupSegment[key] = segmentName
            segmentNamesBySegmentKey[String(groupSegment.segmentKey)] = segmentName
          })
        })
        abTests.forEach((abTest) => {
          const key = String(abTest.segmentKey || '')
          if (!segmentNamesBySegmentKey[key] && segmentationNamesById[key]) {
            segmentNamesBySegmentKey[key] = segmentationNamesById[key]
          }
        })

        if (cancelled) return
        setState({
          status: 'ok',
          data: abTests,
          groupConfigs: groupConfigs || [],
          groupNamesById,
          segmentNamesByGroupSegment,
          segmentNamesBySegmentKey,
          error: null,
        })
      })
      .catch((error) => {
        if (cancelled) return
        setState({
          status: 'error',
          data: [],
          groupConfigs: [],
          groupNamesById: {},
          segmentNamesByGroupSegment: {},
          segmentNamesBySegmentKey: {},
          error,
        })
      })

    return () => {
      cancelled = true
    }
  }, [applicationId])

  const filteredData =
    !filterGroupConfigId || filterGroupConfigId === ''
      ? state.data
      : state.data.filter((test) => String(test.groupConfigId || '') === String(filterGroupConfigId))

  async function handleSetStatus(abTest, status) {
    if (!canTransitionAbTestStatus(abTest.status, status)) {
      toast({
        type: 'error',
        message: `Status transition is not allowed: ${abTest.status} -> ${status}`,
      })
      return
    }
    try {
      const updated = await setAbTestStatus(applicationId, abTest.id, status)
      setState((prev) => ({
        ...prev,
        data: prev.data.map((item) => (String(item.id) === String(updated.id) ? updated : item)),
      }))
    } catch (error) {
      toast({
        type: 'error',
        message: String(error?.message || error),
      })
    }
  }

  return (
    <section className="card sectionCard">
      <div className="pageHeader pageHeaderTight">
        <div>
          <div className="cardTitle">AB tests</div>
        </div>
        <div className="buttonGroup">
          {state.status === 'ok' && state.groupConfigs.length > 0 ? (
            <select
              className="textInput selectFilter"
              value={filterGroupConfigId}
              onChange={(e) => setFilterGroupConfigId(e.target.value)}
              aria-label="Filter by group config"
            >
              <option value="">All group configs</option>
              {state.groupConfigs.map((gc) => (
                <option key={gc.id} value={String(gc.id)}>
                  {gc.name || `Group #${gc.id}`}
                </option>
              ))}
            </select>
          ) : null}
          <Link to={`/applications/${applicationId}/ab-tests/new`} className="button buttonPrimary">
            Create
          </Link>
        </div>
      </div>

      {state.status === 'loading' ? (
        <p className="muted inlineMessage">
          Loading AB tests...
        </p>
      ) : null}

      {state.status === 'error' ? (
        <pre className="codeBlock codeBlockError">{String(state.error?.message || state.error)}</pre>
      ) : null}

      {state.status === 'ok' && state.data.length === 0 ? (
        <p className="muted inlineMessage">
          No AB tests yet.
        </p>
      ) : null}

      {state.status === 'ok' && filteredData.length === 0 && state.data.length > 0 ? (
        <p className="muted inlineMessage">
          No AB tests match the selected filter.
        </p>
      ) : null}

      {state.status === 'ok' && filteredData.length > 0 ? (
        <table className="table" aria-label="AB tests list">
          <thead>
            <tr>
              <th>Name</th>
              <th>Group</th>
              <th>Segment</th>
              <th>Status</th>
              <th>Updated</th>
              <th className="alignRight"></th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((test) => {
              const availableStatuses = getAvailableAbTestStatuses(test)
              const groupName = test.groupConfigName || state.groupNamesById[String(test.groupConfigId)] || test.groupConfigId
              const segmentName =
                test.segmentName ||
                state.segmentNamesByGroupSegment[getGroupSegmentMapKey(test.groupConfigId, test.segmentKey)] ||
                state.segmentNamesBySegmentKey[String(test.segmentKey)] ||
                test.segmentKey

              return (
                <tr key={test.id}>
                  <td>{test.name || 'Untitled'}</td>
                  <td className="muted">{groupName}</td>
                  <td>{segmentName}</td>
                  <td>
                    <select
                      className="textInput textInputCompact"
                      value={test.status}
                      onChange={(e) => handleSetStatus(test, e.target.value)}
                    >
                      {availableStatuses.map((statusValue) => (
                        <option key={statusValue} value={statusValue}>
                          {statusValue}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>{formatDate(test.updatedAt || test.createdAt)}</td>
                  <td className="alignRight">
                    <span className="tableActions">
                      <Link
                        to={`/applications/${applicationId}/ab-tests/${test.id}`}
                        className="button buttonGhost"
                      >
                        View
                      </Link>
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      ) : null}
    </section>
  )
}

